<template>
  <div class="h-full">
    <el-scrollbar
      ref="scrollbarRef"
      v-loading="loading"
      element-loading-background="transparent"
      :style="`height: calc(100% - 28px);`"
    >
      <div class="text-[#344767] font-semibold text-[20px] pl-[24px] pb-[16px]">
        <span>{{ isCreate ? '' : '重新' }}申请连连户</span>
      </div>

      <steps
        :steps="getLianlianSteps(info.formType)"
        :max-width="1400"
        :active-key="isSubmited ? ELianLianStepKey.Submit : currStepKey"
      />

      <!-- 上传营业执照 -->
      <div v-if="currStepKey === ELianLianStepKey.License" class="base-card-info">
        <div class="info-card-title">
          {{ currentName }}
        </div>

        <upload-license
          ref="licenseRef"
          disable-all
          show-legal-name
          :data="info"
          :long-time-format="longTimeValues[0]"
          @key-enter="toNextStep"
        />
      </div>

      <!-- 上传法人信息 -->
      <div v-if="currStepKey === ELianLianStepKey.Legal" class="base-card-info">
        <div class="info-card-title">
          {{ currentName }}
        </div>

        <leagal-person
          ref="legalPersonRef"
          disable-all
          person-label="法定代表人"
          :data="info"
          :name="legalName"
          :long-time-format="longTimeValues[0]"
          @key-enter="toNextStep"
        />
      </div>

      <!-- 上传开户绑卡信息 -->
      <template v-if="currStepKey === ELianLianStepKey.AccOpening">
        <div class="base-card-info">
          <div class="info-card-title">
            {{ currentName }}
          </div>

          <account-opening
            v-if="!isLegalBank"
            ref="accOpeningRef"
            disable-license
            :data="info"
            phone-field-label="银行卡预留手机号"
            @key-enter="toNextStep"
          />

          <binding-card
            v-else
            ref="cardRef"
            :data="{
              phoneNo: info.linkedPhone,
              bankCode: info.bankCode,
              bankNo: info.bankNo,
              bankAccountOpenLicenseUrl: info.bankAccountOpenLicenseUrl,
            }"
            disabled-all
          />
        </div>

        <div class="base-card-info c-auto">
          <div class="info-card-title">
            联系人信息
          </div>

          <contact-person
            ref="contactPersonRef"
            :data="info"
            @key-enter="toNextStep"
          />
        </div>
      </template>

      <!-- 添加受益人 -->
      <div v-if="currStepKey === ELianLianStepKey.Beneficiary" class="base-card-info">
        <div class="info-card-title">
          {{ currentName }}
        </div>

        <beneficiary-info
          ref="beneficiaryInfoRef"
          :data="toRaw(info.uboList)"
          :long-time-format="longTimeValues[0]"
          @key-enter="toNextStep"
        />
      </div>

      <!-- 支付信息认证 -->
      <apply-submit-form
        v-if="currStepKey === ELianLianStepKey.Submit"
        ref="submitFormRef"
        random-path="pay/unified-lian-account/get-random-key"
        random-api-method="post"
        phone-desc="连连开户验证手机号，用于连连开户最后一步意愿确认使用"
        :revalid-fn="validFn"
        :re-valid="needRevalid"
        :send-sms="sendSms"
        :random-query-id="lianId"
        @submit="doSubmit"
      />

      <!-- 已提交 -->
      <div v-if="currStepKey === ELianLianStepKey.Submited" class="base-card-info">
        <account-apply-success
          class="submitted"
          :show-loading="false"
          :bank-info="bankInfo"
          @go-back="closeAndBack"
          @re-apply="() => replaceRoute({ step: ELianLianStepKey.License })"
        />
      </div>
    </el-scrollbar>

    <div v-if="!isSubmited" class="pt-[16px]">
      <el-button @click="closeAndBack">
        取消
      </el-button>
      <el-button v-if="prevStep" @click="toPrevStep">
        上一步
      </el-button>
      <el-button type="primary" :loading="saving" @click="toNextStep">
        {{ submitStep ? '提交' : '下一步' }}
      </el-button>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @stylistic/js/padding-line-between-statements */
import { Steps } from '@vue3/components';
import { computed, ref, toRaw, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElScrollbar, MessageHandler } from 'element-plus';
import { isNull, scrollToErrorField, toElScrollTop, longTimeValues } from '@core/tools';
import { EAccountType, EBankType, ECompanyType, ELianLianStepKey, EUserType, IAgentMergeInfo, ICompanyMergeInfo, IFinanceLianDetail, ILianDetail, IServerMergeInfo } from '@core/main/types';
// eslint-disable-next-line @stylistic/js/max-len
import { getLianlianStep, getLianlianSteps, getMergeFinanceLianInfo, getMergeFinanceOpenedAccInfo, getPrevLianlianStep, queryAgentDetail, queryFinanceLianDetail, sendFinanceLianSmscode, submitFinanceLianLian, submitFinanceBeneficiary, submitFinanceLegal, submitFinanceLianLicense, submitFinanceOpenparam, toLinalianStructure, queryServerDetail, queryComapnyDetail, submitFinanceLianVerify } from '@core/main/api';
import { useTabsStore, useUserStore, IApplySubmitParam, LeagalPerson, AccountOpening, AccountApplySuccess, ApplySubmitForm, BeneficiaryInfo, ContactPerson, BindingCard, UploadLicense } from '@core/main';
import { ESystemType } from '@core/api';

let smsToken = '';
let tokenForSubmit = '';

defineOptions({ name: 'FinanceLianForm' }); // 资金账户 - 申请连连户

const route = useRoute();
const router = useRouter();

const { closeAndBack, editTabName, updatetRouteQuery } = useTabsStore();
const { state: userState } = useUserStore();

const cardRef = ref<InstanceType<typeof BindingCard>>();
const licenseRef = ref<InstanceType<typeof UploadLicense>>();
const legalPersonRef = ref<InstanceType<typeof LeagalPerson>>();
const accOpeningRef = ref<InstanceType<typeof AccountOpening>>();
const contactPersonRef = ref<InstanceType<typeof ContactPerson>>(null);
const beneficiaryInfoRef = ref<InstanceType<typeof BeneficiaryInfo>>(null);

const loading = ref(false);
const saving = ref(false);
const info = ref({} as IFinanceLianDetail);
const bankInfo = ref({} as ILianDetail);
const scrollbarRef = ref<InstanceType<typeof ElScrollbar>>();
const submitFormRef = ref<InstanceType<typeof ApplySubmitForm>>(null);

/** 个体工商户 */
const isProprietor = computed(() => info.value.formType === ECompanyType.Personal);
/** 法人银行卡 */
const isLegalBank = computed(() => info.value.formType === ECompanyType.Personal && info.value.licenseType === 0);
const lianId = computed(() => Number(route.query.lianId));
const isCreate = computed(() => route.query.create === '1');
const currStepKey = computed(() => route.query.step as ELianLianStepKey || ELianLianStepKey.License);
const prevStep = computed(() => getPrevLianlianStep(currStepKey.value, info.value.formType));
const submitStep = computed(() => currStepKey.value === ELianLianStepKey.Submit);
const isSubmited = computed(() => currStepKey.value === ELianLianStepKey.Submited);
const currentName = computed(() => getLianlianStep(currStepKey.value, info.value.formType)?.title);
const legalName = computed(() => route.query.legalName as string || '');
const identity = computed(() => userState.identity);
const needRevalid = computed(() => info.value.formType === ECompanyType.Personal && info.value.licenseType === 0);

const userType = computed(() => {
  if (identity.value.systemType === ESystemType.Agent) return EUserType.AGENT;
  if (identity.value.systemType === ESystemType.Server) return EUserType.SERVER;
  if (identity.value.systemType === ESystemType.Company) return EUserType.COMPANY;

  return undefined;
});

async function fetchBaseDetail() {
  let rs = {} as IAgentMergeInfo | IServerMergeInfo | ICompanyMergeInfo;

  try {
    if (userType.value === EUserType.AGENT) {
      rs = await queryAgentDetail(identity.value.agentId);
    } else if (userType.value === EUserType.SERVER) {
      rs = await queryServerDetail(identity.value.serverId);
    } else if (userType.value === EUserType.COMPANY) {
      rs = await queryComapnyDetail(identity.value.companyId);
    }
  } catch (error) {
    //
  }

  return rs;
}

async function fetchLianDetail() {
  loading.value = true;
  const baseDetail = await fetchBaseDetail();

  if (!lianId.value) { // 新建开户
    info.value = getMergeFinanceLianInfo(baseDetail, undefined, userType.value);
    loading.value = false;
    return;
  }

  try { // 重新申请
    const lianInfo = await queryFinanceLianDetail(lianId.value);
    const mergedDetail = getMergeFinanceLianInfo(baseDetail, lianInfo, userType.value);

    bankInfo.value = toLinalianStructure(lianInfo);
    info.value = getMergeFinanceOpenedAccInfo(mergedDetail);
  } catch (error) {
    //
  }
  loading.value = false;
}

async function sendSms(phone: string) {
  smsToken = await sendFinanceLianSmscode(phone, lianId.value);
}

async function validFn() {
  const values = submitFormRef.value?.getValues();

  tokenForSubmit = await submitFinanceLianLian({
    lianId: lianId.value,
    token: smsToken,
    regPhone: values.phone,
    verifyCode: values.code,
    password: values.password,
    randomKey: submitFormRef.value?.getRandomKeys()?.randomKey,
  });
}

async function doSubmit(values: IApplySubmitParam) {
  saving.value = true;
  try {
    if (!needRevalid.value) {
      await submitFinanceLianLian({
        lianId: lianId.value,
        token: smsToken,
        regPhone: values.phone,
        verifyCode: values.code,
        password: values.password,
        randomKey: submitFormRef.value?.getRandomKeys()?.randomKey,
      });
    } else {
      await submitFinanceLianVerify({
        lianId: lianId.value,
        verifyCode: values.verifyCode,
        token: tokenForSubmit,
        password: values.password,
        randomKey: submitFormRef.value?.getRandomKeys()?.randomKey,
      });
    }

    bankInfo.value = await queryFinanceLianDetail(lianId.value);
    replaceRoute({ step: ELianLianStepKey.Submited });
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  saving.value = false;
}

async function saveLicenseInfo() {
  const valid = await licenseRef.value?.validate();
  if (!valid) return;

  saveHandler(
    async () => {
      const d = await submitFinanceLianLicense({
        ...getLianCodes(),
        ...licenseRef.value?.getValues(),
        userType: userType.value,
        accountType: EAccountType.ENTERPRISE_PAYMENT,
        type: info.value.formType,
      });
      const pushId = d.lianId || lianId.value || undefined;

      if (!pushId) return ElMessage.error('服务端未返回lianId，请检查');

      info.value = {
        ...toRaw(info.value),
        lianId: pushId,
        bankType: d.bankType,
      };

      replaceRoute({
        lianId: pushId,
        step: ELianLianStepKey.Legal,
      });
    },
  );
}

async function saveLegalInfo() {
  const valid = await legalPersonRef.value?.validate();
  if (!valid) return;

  saveHandler(
    async () => {
      await submitFinanceLegal({
        ...getLianCodes(),
        ...legalPersonRef.value?.getValues(),
      });

      replaceRoute({ step: ELianLianStepKey.AccOpening });
    },
  );
}

async function saveAccOpeningInfo() {
  if (!isLegalBank.value) {
    const accountOpenValid = await accOpeningRef.value?.validate();
    if (!accountOpenValid) return;
  } else {
    const cardValid = await cardRef.value?.validate();
    if (!cardValid) return;
  }

  const contactPersonValid = await contactPersonRef.value?.validate();
  if (!contactPersonValid) return;

  saveHandler(
    async () => {
      let accopen = !isLegalBank.value ? accOpeningRef.value?.getValues() : {};
      const contact = contactPersonRef.value?.getValues();

      if (isLegalBank.value) {
        const cardVal = cardRef.value?.getValues();
        // @ts-ignore
        accopen = {
          bankNo: cardVal.bankNo,
          bankCode: cardVal.bankCode,
          linkedPhone: cardVal.phoneNo,
          bankAccountOpenLicenseUrl: cardVal.bankAccountOpenLicenseUrl,
        };
      }

      await submitFinanceOpenparam({
        ...getLianCodes(),
        ...accopen,
        ...contact,
        bankType: info.value.licenseType === 0 ? EBankType.LEGALREPT : EBankType.ENTERPRISE,
      });

      info.value = {
        ...toRaw(info.value),
        ...accopen,
        ...contact,
      };

      replaceRoute({
        step: !isProprietor.value ? ELianLianStepKey.Beneficiary : ELianLianStepKey.Submit,
        reservedPhone: info.value.linkedPhone,
      });
    },
  );
}

async function saveBeneficiaryInfo() {
  const valid = await beneficiaryInfoRef.value?.validate();
  if (!valid) return;

  saveHandler(
    async () => {
      await submitFinanceBeneficiary({
        ...getLianCodes(),
        uboList: beneficiaryInfoRef.value?.getValues(),
      });

      info.value = {
        ...toRaw(info.value),
        uboList: beneficiaryInfoRef.value?.getValues(),
      };

      replaceRoute({ step: ELianLianStepKey.Submit });
    },
  );
}

function getLianCodes() {
  return { lianId: lianId.value ? lianId.value : undefined };
}

function replaceRoute(v: Record<string, any>) {
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      ...v,
    },
  });
  updatetRouteQuery({ ...v });
  toElScrollTop();
}

function handleSubmit() {
  submitFormRef.value?.validateForm(
    doSubmit,
    () => {
      scrollToErrorField(submitFormRef.value?.$el);
    },
  );
}

async function saveHandler(callback: () => Promise<void | MessageHandler>) {
  saving.value = true;

  try {
    await callback();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  saving.value = false;
}

function toPrevStep() {
  const prevStep = getPrevLianlianStep(currStepKey.value, info.value.formType);

  replaceRoute({ step: prevStep.key });
}

function toNextStep() {
  if (submitStep.value) return handleSubmit();

  switch (currStepKey.value) {
    case ELianLianStepKey.License:
      saveLicenseInfo();
      break;
    case ELianLianStepKey.Legal:
      saveLegalInfo();
      break;
    case ELianLianStepKey.AccOpening:
      saveAccOpeningInfo();
      break;
    case ELianLianStepKey.Beneficiary:
      saveBeneficiaryInfo();
      break;
    default:
      break;
  }
}

watch(
  () => userType.value,
  (type) => {
    if (!isNull(type)) {
      console.log('detail', type);
      fetchLianDetail();
    }
  },
  { immediate: true },
);

editTabName(`${isCreate.value ? '' : '重新'}申请连连户`);
</script>

<style lang="scss" scoped>
:deep(.box-content) {
  padding: 50px 0 80px;
}

.base-card-info {
  min-height: calc(100vh - 393px);

  &.c-auto {
    min-height: unset;
  }
}

.submitted {
  min-height: calc(100vh - 420px) !important;
}

:deep(.steps-items-box) {
  min-height: unset !important;
}
</style>
